// src/pages/clab-dashboard/index.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import StatusIndicatorBadge from 'components/ui/StatusIndicatorBadge';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import { getUserRoleForComponents } from 'utils/authHelpers';
import { clabAPI, biodataAPI } from 'utils/api';
import { useBiodataCache } from 'context/BiodataCacheContext';

const ClabDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const { getAllBiodatas } = useBiodataCache();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Memoize user role check to prevent unnecessary recalculations
  const isAuthorizedUser = useMemo(() => {
    if (!userProfile) return false;
    const allowedRoles = ['clab_user', 'clab', 'admin', 'master_admin', '1', 1];
    return allowedRoles.includes(userProfile.role) || allowedRoles.includes(userProfile.role?.toLowerCase());
  }, [userProfile?.role]);

  // Load dashboard data - simple function without useCallback
  const loadDashboardData = async () => {
    if (hasLoaded) return; // Prevent duplicate loads
    
    try {
      setLoading(true);
      setError(null);

      // Load dashboard stats
      const dashboardResult = await clabAPI.dashboard();
      
      if (dashboardResult.status === 'success') {
        setStats(dashboardResult.data);
      } else {
        throw new Error(dashboardResult.message || 'Failed to load dashboard data');
      }

      // Load recent biodata for activity feed
      const recentResult = await getAllBiodatas();
      
      if (recentResult.fromCache) {
      }

      if (recentResult.success) {
        setRecentActivity(recentResult.records || []);
      }

      setHasLoaded(true); // Mark as loaded to prevent reloading
    } catch (error) {
      console.error('❌ Dashboard loading error:', error);
      setError('Failed to load dashboard data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Simple useEffect that runs once on component mount
  useEffect(() => {
    loadDashboardData();
  }, []); // Only run once when component mounts

  const getStatusColor = useCallback((status) => {
    const statusColors = {
      'submitted': 'warning',
      'under_review': 'accent',
      'approved': 'success',
      'rejected': 'error',
      'selected': 'primary'
    };
    return statusColors[status?.toLowerCase()] || 'secondary';
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // If no user profile yet, show loading
  if (!userProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-text-secondary">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar 
          userRole={userRole} 
          userName={userProfile?.full_name || 'CLAB User'}
          onSidebarToggle={handleSidebarToggle} 
        />
        
        <RoleBasedSidebar 
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <main className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-text-secondary">Loading dashboard data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar 
          userRole={userRole} 
          userName={userProfile?.full_name || 'CLAB User'}
          onSidebarToggle={handleSidebarToggle} 
        />
        
        <RoleBasedSidebar 
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <main className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-6">
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <h3 className="font-semibold text-error-800">Error Loading Dashboard</h3>
              </div>
              <p className="text-error-700 mt-2">{error}</p>
              <button
                onClick={() => {
                  setHasLoaded(false);
                  setError(null);
                  loadDashboardData();
                }}
                className="mt-3 px-4 py-2 bg-error text-white rounded-md hover:bg-error-600 transition-colors duration-150"
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar 
        userRole={userRole} 
        userName={userProfile?.full_name || 'CLAB User'}
        onSidebarToggle={handleSidebarToggle} 
      />
      
      <RoleBasedSidebar 
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main className={`transition-all duration-300 pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">CLAB Dashboard</h1>
            <p className="text-text-secondary">Monitor and manage foreign worker biodata review process</p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Pending Review</p>
                  <p className="text-3xl font-bold text-warning">{stats?.biodata_stats?.pending || 0}</p>
                </div>
                <div className="p-3 bg-warning-100 rounded-full">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/clab-biodata-pending')}
                  className="text-sm text-warning hover:text-warning-700 font-medium"
                >
                  Review Now →
                </button>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Approved</p>
                  <p className="text-3xl font-bold text-success">{stats?.biodata_stats?.approved || 0}</p>
                </div>
                <div className="p-3 bg-success-100 rounded-full">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/clab-biodata-approved')}
                  className="text-sm text-success hover:text-success-700 font-medium"
                >
                  View All →
                </button>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Rejected</p>
                  <p className="text-3xl font-bold text-error">{stats?.biodata_stats?.rejected || 0}</p>
                </div>
                <div className="p-3 bg-error-100 rounded-full">
                  <Icon name="XCircle" size={24} className="text-error" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/clab-biodata-rejected')}
                  className="text-sm text-error hover:text-error-700 font-medium"
                >
                  View All →
                </button>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Pending Applications</p>
                  <p className="text-3xl font-bold text-primary">{stats?.application_stats?.pending || 0}</p>
                </div>
                <div className="p-3 bg-primary-100 rounded-full">
                  <Icon name="FileCheck" size={24} className="text-primary" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/clab-selection-requests')}
                  className="text-sm text-primary hover:text-primary-700 font-medium"
                >
                  Review Now →
                </button>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Unselected Workers</p>
                  <p className="text-3xl font-bold text-indigo-600">{stats?.biodata_stats?.unselected || 0}</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-full">
                  <Icon name="Users" size={24} className="text-indigo-600" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/clab-unselected-workers')}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Manage Pool →
                </button>
              </div>
            </div>
          </div>

          {/* Additional Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Approved Applications</p>
                  <p className="text-2xl font-bold text-success">{stats?.application_stats?.approved || 0}</p>
                </div>
                <div className="p-3 bg-success-100 rounded-full">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Active Shortlists</p>
                  <p className="text-2xl font-bold text-accent">{stats?.shortlist_stats?.active || 0}</p>
                </div>
                <div className="p-3 bg-accent-100 rounded-full">
                  <Icon name="UserCheck" size={20} className="text-accent" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Expired Shortlists</p>
                  <p className="text-2xl font-bold text-warning">{(stats?.shortlist_stats?.expired || 0) + (stats?.shortlist_stats?.expired_24h || 0)}</p>
                </div>
                <div className="p-3 bg-warning-100 rounded-full">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          {stats?.performance_metrics && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="card p-6">
                <div className="text-center">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">Approval Rate</h3>
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto bg-success-100 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-success">{stats.performance_metrics.approval_rate}%</span>
                    </div>
                  </div>
                  <p className="text-xs text-text-secondary mt-2">Of all reviewed biodatas</p>
                </div>
              </div>
              
              <div className="card p-6">
                <div className="text-center">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">Rejection Rate</h3>
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto bg-error-100 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-error">{stats.performance_metrics.rejection_rate}%</span>
                    </div>
                  </div>
                  <p className="text-xs text-text-secondary mt-2">Of all reviewed biodatas</p>
                </div>
              </div>
              
              <div className="card p-6">
                <div className="text-center">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">Avg Processing Time</h3>
                  <div className="relative">
                    <div className="w-16 h-16 mx-auto bg-accent-100 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-accent">{stats.avg_processing_hours}h</span>
                    </div>
                  </div>
                  <p className="text-xs text-text-secondary mt-2">From submission to review</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-text-primary">Recent Activity (7 days)</h2>
                  <button
                    onClick={() => navigate('/clab-biodata-pending')}
                    className="text-sm text-primary hover:text-primary-700 font-medium"
                  >
                    View All
                  </button>
                </div>
              </div>
              <div className="p-6">
                {stats?.recent_activity ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Icon name="Upload" size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">Pending Biodatas</p>
                          <p className="text-sm text-text-secondary">Currently awaiting review</p>
                          {typeof stats.recent_activity.new_submissions === 'number' && (
                            <p className="text-xs text-secondary-500 mt-1">
                              New submissions (7d): {stats.recent_activity.new_submissions}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{stats.recent_activity.biodatas_submitted}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Icon name="CheckCircle" size={20} className="text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">Biodatas Approved</p>
                          <p className="text-sm text-text-secondary">Approved this week</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">{stats.recent_activity.biodatas_approved}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <Icon name="XCircle" size={20} className="text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">Biodatas Rejected</p>
                          <p className="text-sm text-text-secondary">Rejected this week</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-600">{stats.recent_activity.biodatas_rejected}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Icon name="FileText" size={20} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">Applications Submitted</p>
                          <p className="text-sm text-text-secondary">New applications this week</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-600">{stats.recent_activity.applications_submitted}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Icon name="Users" size={20} className="text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">Available for Selection</p>
                          <p className="text-sm text-text-secondary">Workers in unselected pool</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-600">
                          {stats.recent_activity.unselected_workers ?? stats.biodata_stats?.unselected ?? 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Activity" size={48} className="text-secondary-300 mx-auto mb-4" />
                    <p className="text-text-secondary">No recent activity data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Biodata by Nationality */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h2 className="text-xl font-semibold text-text-primary">Biodata by Nationality</h2>
              </div>
              <div className="p-6">
                {stats?.by_nationality && Object.keys(stats.by_nationality).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(stats.by_nationality)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 6)
                      .map(([nationality, count]) => {
                        const total = stats.biodata_stats?.total || 0;
                        const percentage = total > 0 ? (count / total * 100).toFixed(1) : 0;
                        return (
                          <div key={nationality} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-text-primary">{nationality}</span>
                              <span className="text-sm text-text-secondary">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-secondary-200 rounded-full h-2">
                              <div 
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="BarChart3" size={48} className="text-secondary-300 mx-auto mb-4" />
                    <p className="text-text-secondary">No data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Rejection Reasons */}
          {stats?.top_rejection_reasons && stats.top_rejection_reasons.length > 0 && (
            <div className="card mt-6">
              <div className="p-6 border-b border-secondary-200">
                <h2 className="text-xl font-semibold text-text-primary">Top Rejection Reasons</h2>
                <p className="text-sm text-text-secondary mt-1">Most common reasons for biodata and worker rejections</p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {stats.top_rejection_reasons.map((reasonData, index) => {
                    const maxCount = stats.top_rejection_reasons[0]?.total_count || 1;
                    const percentage = (reasonData.total_count / maxCount * 100).toFixed(1);
                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-error-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-error">#{index + 1}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-text-primary">{reasonData.reason}</span>
                              <div className="flex items-center space-x-2 text-xs text-text-secondary">
                                <span>Biodata: {reasonData.biodata_count}</span>
                                <span>•</span>
                                <span>Workers: {reasonData.worker_count}</span>
                              </div>
                            </div>
                          </div>
                          <span className="text-sm font-medium text-text-primary">{reasonData.total_count} total</span>
                        </div>
                        <div className="w-full bg-secondary-200 rounded-full h-2">
                          <div 
                            className="bg-error h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClabDashboard;
